import React from 'react';
import {
  Card,
  Typography,
  Tag,
  Skeleton,
} from 'antd';
import { UserOutlined, RobotOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { ChatMessage as ChatMessageType } from '../../types';
import { markdownToHtml, hasMarkdown } from '../../utils/markdown';
import { useAuthStore } from '../../store/auth';
import { useChatStore } from '../../store/chat';
import { useProfilePicture } from '../../hooks/useProfilePicture';
import { BouncingDots } from './BouncingDots';
import { EmailBodyRenderer } from '../Email/EmailBodyRenderer';

interface ChatMessageProps {
  message: ChatMessageType;
}

export const ChatMessage: React.FC<ChatMessageProps> = ({ message }) => {

  const { user } = useAuthStore();
  const { settings, isStreaming, currentSession } = useChatStore();
  const { profilePicture, isLoading: isLoadingPicture } = useProfilePicture();
  
  const isUser = message.role === 'user';
  const isAssistant = message.role === 'assistant';
  const isSystem = message.role === 'system';
  const isTool = message.role === 'tool';

  // Function to detect if a tool result is an email body with HTML content
  const isEmailBodyResult = (result: any) => {
    return result.name === 'get_email_body' && result.content;
  };

  // Function to parse email body content (handles both old string format and new object format)
  const parseEmailBodyContent = (content: string) => {
    try {
      // Try to parse as JSON first (new format)
      const parsed = JSON.parse(content);
      if (parsed && typeof parsed === 'object' && parsed.html !== undefined) {
        return parsed;
      }
    } catch (e) {
      // If JSON parsing fails, treat as plain text (old format)
    }
    
    // Return as string for backwards compatibility
    return content;
  };
  
  // Get actual names
  const userName = user?.name?.split(' ')[0] || 'User'; // Just first name
  const aiName = settings?.aiName || 'AI Assistant';

  // Function to detect if message content contains email body data
  const detectEmailBodyInContent = (content: string) => {
    // Look for patterns that indicate email body content in the message
    const emailBodyPatterns = [
      /Email Body \(HTML\)/i,
      /Email Content[\s\S]*?HTML Version:/i,
      /<html[^>]*>[\s\S]*<\/html>/i,
      /From:.*To:.*Subject:.*Email Body/is,
      /HTML Version:[\s\S]*?<html/i,
      // New patterns for the current format
      /Here is the body of the last email received:/i,
      /\*\*From:\*\*.*\*\*Sent:\*\*.*\*\*To:\*\*/is,
      /---[\s]*\*\*From:\*\*/i
    ];
    
    return emailBodyPatterns.some(pattern => pattern.test(content));
  };

  // Function to extract email body data from content
  const extractEmailBodyFromContent = (content: string) => {
    // Try to find HTML content between <html> tags (old format)
    const htmlMatch = content.match(/<html[^>]*>([\s\S]*?)<\/html>/i);
    if (htmlMatch) {
      const htmlContent = `<html>${htmlMatch[1]}</html>`;
      
      // Convert HTML to plain text for display
      const text = htmlContent
        .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '') // Remove style blocks
        .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '') // Remove script blocks
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<\/p>/gi, '\n\n')
        .replace(/<\/div>/gi, '\n')
        .replace(/<\/h[1-6]>/gi, '\n\n')
        .replace(/<\/tr>/gi, '\n')
        .replace(/<\/td>/gi, ' ')
        .replace(/<[^>]*>/g, '')
        .replace(/&nbsp;/g, ' ')
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
        .replace(/\n\s*\n\s*\n/g, '\n\n')
        .replace(/\s+/g, ' ') // Collapse multiple spaces
        .trim();

      return {
        html: htmlContent,
        text: text,
        contentType: 'HTML'
      };
    }

    // Handle new format - extract email content from markdown-style format
    const emailBodyMatch = content.match(/Here is the body of the last email received:([\s\S]*)/i);
    if (emailBodyMatch) {
      const emailContent = emailBodyMatch[1].trim();
      
      // For now, we don't have HTML in this format, so return the formatted text
      // The HTML will need to come from a separate API call or tool result
      return {
        html: null, // No HTML available in this format
        text: emailContent,
        contentType: 'TEXT'
      };
    }
    
    return null;
  };

  const renderContent = () => {
    if (!message.content && isAssistant) {
      // Check if this is the last message and we're streaming
      const messages = currentSession?.messages || [];
      const isLastMessage = messages[messages.length - 1]?.id === message.id;
      
      if (isStreaming && isLastMessage) {
        return <BouncingDots />;
      } else {
        return <Skeleton active paragraph={{ rows: 2 }} title={false} />;
      }
    }

    // Ensure content is a string
    let content = message.content;
    if (typeof content !== 'string') {
      console.error('ChatMessage received non-string content:', typeof content, content);
      
      if (content && typeof content === 'object') {
        if (typeof content.toString === 'function') {
          content = content.toString();
        } else {
          console.error('Cannot convert message content object to string:', content);
          content = '[Unable to display content]';
        }
      } else {
        content = String(content || '');
      }
    }

    if (!content) {
      return null;
    }

    // Check if content contains email body data
    if (detectEmailBodyInContent(content)) {
      const emailBodyData = extractEmailBodyFromContent(content);
      if (emailBodyData) {
        // Extract the intro text (everything before the email content)
        let introText = '';
        const introMatch = content.match(/([\s\S]*?)(?=Here is the body of the last email received:|---[\s]*\*\*From:\*\*)/i);
        if (introMatch) {
          introText = introMatch[1].trim();
        }
        
        return (
          <div>
            {introText && (
              <div style={{ 
                whiteSpace: 'pre-wrap', 
                marginBottom: '1rem',
                color: 'var(--color-text-primary)'
              }}>
                {introText}
              </div>
            )}
            <div style={{
              backgroundColor: 'var(--color-bg-tertiary)',
              border: '1px solid var(--color-border-primary)',
              borderRadius: 'var(--radius-md)',
              padding: '0.75rem',
              fontSize: '0.875rem'
            }}>
              <div style={{ 
                marginBottom: '8px', 
                fontSize: '12px', 
                color: 'var(--color-text-secondary)',
                fontWeight: 500 
              }}>
                Email Body:
              </div>
              <div style={{ 
                whiteSpace: 'pre-wrap',
                fontFamily: 'inherit',
                lineHeight: '1.4',
                fontSize: '13px'
              }}>
                {emailBodyData.text}
              </div>
            </div>
          </div>
        );
      }
    }

    // Check if content has any markdown formatting
    if (hasMarkdown(content)) {
      return (
        <div 
          dangerouslySetInnerHTML={{ __html: markdownToHtml(content) }}
        />
      );
    }

    // Plain text content with preserved line breaks
    return (
      <div style={{ 
        whiteSpace: 'pre-wrap', 
        margin: 0, 
        padding: 0,
        color: 'var(--color-text-primary)'
      }}>
        {content.trim()}
      </div>
    );
  };

  const getUserIcon = () => {
    // Show profile picture if available, otherwise show icon
    if (profilePicture && !isLoadingPicture) {
      return (
        <img 
          src={profilePicture} 
          alt="Profile" 
          style={{ 
            width: '19px', 
            height: '19px', 
            borderRadius: '50%',
            objectFit: 'cover',
            marginRight: '8px'
          }} 
          onError={(e) => {
            console.log('Profile picture failed to load, falling back to icon');
            e.currentTarget.style.display = 'none';
            const parent = e.currentTarget.parentElement;
            if (parent) {
              parent.innerHTML = '';
              const icon = document.createElement('div');
              parent.appendChild(icon);
              // This is a bit hacky, but we need to render the React icon somehow
            }
          }}
        />
      );
    }
    
    return <UserOutlined style={{ fontSize: '19px', marginRight: '8px' }} />;
  };

  const getMessageIcon = () => {
    if (isUser) return getUserIcon();
    if (isAssistant) return <RobotOutlined style={{ fontSize: '19px', marginRight: '8px' }} />;
    return null;
  };

  const getMessageStyle = () => {
    if (isSystem) {
      return {
        backgroundColor: 'var(--color-bg-inverse)',
        color: 'var(--color-text-inverse)',
        fontSize: '0.875rem',
        fontStyle: 'italic',
      };
    }
    
    if (isTool) {
      return {
        backgroundColor: 'var(--color-bg-secondary)',
        borderLeft: '3px solid var(--color-primary)',
      };
    }

    return {
      backgroundColor: 'var(--color-chat-message-bg)', // Match workspace background
    };
  };

  // Skip rendering system and tool messages for now
  if (isSystem || isTool) {
    return null;
  }

  // Skip rendering empty assistant messages ONLY if they're not the last message during streaming
  // This prevents empty messages from restored chats but allows streaming indicators
  if (isAssistant && !message.content && !message.toolCalls?.length && !message.toolResults?.length) {
    const messages = currentSession?.messages || [];
    const isLastMessage = messages[messages.length - 1]?.id === message.id;
    
    // Only hide if not streaming or not the last message
    if (!isStreaming || !isLastMessage) {
      return null;
    }
  }

  return (
    <div className={`chat-message chat-message--${message.role}`} style={{
      display: 'flex',
      justifyContent: isAssistant ? 'flex-end' : 'flex-start',
      marginBottom: '1.5rem',
      marginTop: '2rem', // Space for the overlapping badge
      marginLeft: '24px',
      marginRight: '24px',
      width: 'calc(100% - 48px)'
    }}>
      <div style={{
        maxWidth: '90%',
        minWidth: '210px', // Increased by another 10px
        position: 'relative',
        overflow: 'visible' // Ensure badges don't get clipped
      }}>
        {/* Message bubble */}
        <div style={{
          ...getMessageStyle(),
          borderRadius: '12px',
          padding: '0.75rem 1rem',
          border: '1px solid var(--color-chat-message-border)',
          boxShadow: 'var(--color-chat-message-shadow)'
        }}>
          <div className="message-content" style={{ 
            margin: 0,
            textAlign: 'left',
            paddingTop: '8px',
            color: 'var(--color-text-primary)',
            fontSize: '0.875rem'
          }}>
            {renderContent()}
          </div>

          {/* Status indicator for queued/processing messages */}
          {message.status === 'queued' && (
            <div style={{ 
              marginTop: '8px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontSize: '12px',
              color: 'var(--color-text-secondary)',
              fontStyle: 'italic'
            }}>
              <ClockCircleOutlined />
              <span>Queued - will be sent when current message completes</span>
            </div>
          )}
          {message.status === 'processing' && (
            <div style={{ 
              marginTop: '8px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontSize: '12px',
              color: 'var(--color-primary)',
              fontStyle: 'italic'
            }}>
              <ClockCircleOutlined spin />
              <span>Processing...</span>
            </div>
          )}

          {/* Tool calls */}
          {message.toolCalls && message.toolCalls.length > 0 && (
            <div className="tool-calls" style={{ marginTop: '1rem' }}>
              <h6 style={{ margin: '0 0 0.5rem 0', fontSize: 'var(--font-size-md)', color: 'var(--color-text-secondary)' }}>
                Tool Calls:
              </h6>
              {message.toolCalls.map((toolCall) => (
                <div key={toolCall.id} className="tool-call" style={{ marginBottom: '0.5rem' }}>
                  <Tag type="purple" size="sm">
                    {toolCall.name}
                  </Tag>
                  <span style={{ marginLeft: 'var(--spacing-sm)', fontSize: 'var(--font-size-md)', color: 'var(--color-text-secondary)' }}>
                    {toolCall.status}
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* Tool results */}
          {message.toolResults && message.toolResults.length > 0 && (
            <div className="tool-results" style={{ marginTop: '1rem' }}>
              <h6 style={{ margin: '0 0 0.5rem 0', fontSize: 'var(--font-size-md)', color: 'var(--color-text-secondary)' }}>
                Tool Results:
              </h6>
              {message.toolResults.map((result) => {
                // Check if this is an email body result
                if (isEmailBodyResult(result)) {
                  const emailBodyData = parseEmailBodyContent(result.content);
                  
                  return (
                    <div key={result.id} style={{
                      marginBottom: '0.5rem',
                      backgroundColor: 'var(--color-bg-tertiary)',
                      border: '1px solid var(--color-border-primary)',
                      borderRadius: 'var(--radius-md)',
                      padding: '0.75rem',
                      fontSize: '0.875rem'
                    }}>
                      <div style={{ 
                        marginBottom: '8px', 
                        fontSize: '12px', 
                        color: 'var(--color-text-secondary)',
                        fontWeight: 500 
                      }}>
                        Email Body:
                      </div>
                      <EmailBodyRenderer 
                        emailBody={emailBodyData} 
                        compact={true}
                      />
                    </div>
                  );
                }
                
                // Regular tool result rendering
                return (
                  <div key={result.id} style={{
                    marginBottom: '0.5rem',
                    backgroundColor: 'var(--color-bg-tertiary)',
                    border: '1px solid var(--color-border-primary)',
                    borderRadius: 'var(--radius-md)',
                    padding: '0.75rem',
                    fontSize: '0.875rem',
                    fontFamily: 'monospace',
                    whiteSpace: 'pre-wrap',
                    overflow: 'auto'
                  }}>
                    {result.content}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Name badge positioned at bubble outer edge */}
        <div style={{
          position: 'absolute',
          top: '-24px', // Move up by 25% more (was -16px for 50%, now -24px for 75% overlap)
          [isAssistant ? 'right' : 'left']: '-16px', // Move outward by border-radius amount (12px)
          zIndex: 5 // Lower z-index so timestamp appears over badge
        }}>
          <Tag
            color={isUser ? 'green' : 'grey'}
            size="sm"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0.25rem 0.75rem', // Added 4px more horizontal padding
              height: 'auto',
              minHeight: '32px',
              lineHeight: '1'
            }}
          >
            <div style={{ 
              display: 'flex', 
              alignItems: 'center',
              height: '100%'
            }}>
              {getMessageIcon()}
              <span style={{ 
                display: 'flex', 
                alignItems: 'center',
                lineHeight: '1',
                fontSize: '0.875rem'
              }}>
                {isUser ? userName : aiName}
              </span>
            </div>
          </Tag>
        </div>

        {/* Timestamp positioned on inside of badge (right of user badge, left of assistant badge) */}
        <div style={{
          position: 'absolute',
          top: '-24px', // Match the new badge top position
          ...(isAssistant ? { left: '10px' } : { left: '140px' }), // For user: position to the right of the badge, for assistant: left as before
          display: 'flex',
          alignItems: 'center',
          height: '32px', // Same as badge height
          zIndex: 10 // Higher z-index to appear over badge
        }}>
          <span style={{
            fontSize: '0.75rem',
            color: 'var(--color-text-tertiary)'
          }}>
            {message.timestamp.toLocaleTimeString()}
          </span>
        </div>
      </div>
    </div>
  );
};